import {
  IconAlertCircle,
  IconDownload,
  IconExternalLink,
  IconFileTypePdf,
  IconLoader2,
  IconRefresh,
  IconX,
} from "@tabler/icons-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useInvoicesPdfMutation, warmupInvoicesPdf } from "../../api/queries/paymentsQueries";
import {
  base64ToPdfBlobUrl,
  invoicePdfFilename,
} from "../../utils/utilsFunctions/functions";

type InvoicePdfState =
  | { status: "loading" }
  | { status: "success"; url: string }
  | { status: "error"; message: string };

type InvoiceFilesModalProps = {
  invoiceIds: string[];
  isOpen: boolean;
  onClose: () => void;
};

function revokePdfUrls(states: Record<string, InvoicePdfState>) {
  Object.values(states).forEach((state) => {
    if (state.status === "success") {
      URL.revokeObjectURL(state.url);
    }
  });
}

export function InvoiceFilesModal({
  invoiceIds,
  isOpen,
  onClose,
}: InvoiceFilesModalProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [pdfStates, setPdfStates] = useState<Record<string, InvoicePdfState>>(
    {},
  );
  const pdfStatesRef = useRef(pdfStates);
  pdfStatesRef.current = pdfStates;
  const loadGenerationRef = useRef(0);

  const { mutateAsync: fetchPdfs } = useInvoicesPdfMutation();

  const applyPdfResult = useCallback(
    (
      id: string,
      result: { status: string; pdf_base64?: string; error?: string },
    ) => {
      if (result.status === "success" && result.pdf_base64) {
        try {
          return {
            status: "success" as const,
            url: base64ToPdfBlobUrl(id, result.pdf_base64),
          };
        } catch {
          return {
            status: "error" as const,
            message: "Could not render this PDF.",
          };
        }
      }

      const rawError = result.error ?? "Invoice file not available.";
      const message = /rate limit|concurrent|429/i.test(rawError)
        ? "Accounting software is busy. Wait a moment and try again."
        : rawError;

      return { status: "error" as const, message };
    },
    [],
  );

  const applyFetchResults = useCallback(
    (
      ids: string[],
      response: { results: Array<{ external_id: string; status: string; pdf_base64?: string; error?: string }> },
    ) => {
      const resultsById = new Map(
        response.results.map((result) => [result.external_id, result]),
      );

      setPdfStates((prev) => {
        const next = { ...prev };
        for (const id of ids) {
          const result = resultsById.get(id);
          const nextState = result
            ? applyPdfResult(id, result)
            : {
                status: "error" as const,
                message: "Invoice file not available.",
              };
          const previous = next[id];
          if (
            previous?.status === "success" &&
            nextState.status === "success"
          ) {
            URL.revokeObjectURL(previous.url);
          }
          next[id] = nextState;
        }
        return next;
      });
    },
    [applyPdfResult],
  );

  const loadPdfs = useCallback(
    async (
      ids: string[],
      generation: number,
      options?: { replaceAll?: boolean },
    ) => {
      if (ids.length === 0) return;

      if (options?.replaceAll) {
        revokePdfUrls(pdfStatesRef.current);
        setPdfStates(
          Object.fromEntries(
            ids.map((id) => [id, { status: "loading" as const }]),
          ),
        );
      } else {
        setPdfStates((prev) => {
          const next = { ...prev };
          ids.forEach((id) => {
            next[id] = { status: "loading" };
          });
          return next;
        });
      }

      try {
        const response = await fetchPdfs(ids);
        if (loadGenerationRef.current !== generation) return;
        applyFetchResults(ids, response);
      } catch {
        if (loadGenerationRef.current !== generation) return;
        setPdfStates((prev) => {
          const next = { ...prev };
          ids.forEach((id) => {
            next[id] = {
              status: "error",
              message: "Failed to load invoice file. Please try again.",
            };
          });
          return next;
        });
      }
    },
    [applyFetchResults, fetchPdfs],
  );

  useEffect(() => {
    if (!isOpen || invoiceIds.length === 0) return;
    const generation = ++loadGenerationRef.current;
    setActiveIndex(0);
    void warmupInvoicesPdf().catch(() => {});
    void loadPdfs(invoiceIds, generation, { replaceAll: true });
  }, [isOpen, invoiceIds, loadPdfs]);

  useEffect(() => {
    if (isOpen) return;
    revokePdfUrls(pdfStatesRef.current);
    setPdfStates({});
    setActiveIndex(0);
  }, [isOpen]);

  useEffect(() => {
    return () => revokePdfUrls(pdfStatesRef.current);
  }, []);

  const loadingCount = Object.values(pdfStates).filter(
    (s) => s.status === "loading",
  ).length;
  const isFetchingBatch = loadingCount > 0;

  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !isFetchingBatch) {
        onClose();
      }
    };

    const handleTabNav = (event: KeyboardEvent) => {
      if (invoiceIds.length <= 1) return;
      if (event.key === "ArrowLeft") {
        event.preventDefault();
        setActiveIndex((i) => Math.max(0, i - 1));
      } else if (event.key === "ArrowRight") {
        event.preventDefault();
        setActiveIndex((i) => Math.min(invoiceIds.length - 1, i + 1));
      }
    };

    document.addEventListener("keydown", handleEscape);
    document.addEventListener("keydown", handleTabNav);
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("keydown", handleTabNav);
    };
  }, [isOpen, isFetchingBatch, invoiceIds.length, onClose]);

  if (!isOpen || invoiceIds.length === 0) return null;

  const activeId = invoiceIds[activeIndex] ?? invoiceIds[0];
  const activeState = pdfStates[activeId] ?? { status: "loading" as const };
  const hasMultiple = invoiceIds.length > 1;
  const loadedCount = Object.values(pdfStates).filter(
    (s) => s.status === "success",
  ).length;
  const errorCount = Object.values(pdfStates).filter(
    (s) => s.status === "error",
  ).length;

  const handleDownload = () => {
    if (activeState.status !== "success") return;
    const link = document.createElement("a");
    link.href = activeState.url;
    link.download = invoicePdfFilename(activeId);
    link.click();
  };

  const handleOpenInNewTab = () => {
    if (activeState.status !== "success") return;
    window.open(activeState.url, "_blank", "noopener,noreferrer");
  };

  // Portal above AppShell.Header — in-tree fixed overlays sit under the top menu.
  return createPortal(
    <div className="fixed inset-0 z-200 flex items-center justify-center p-4 sm:p-6">
      <button
        type="button"
        aria-label="Close invoice files"
        className="absolute inset-0 bg-slate-900/55 backdrop-blur-[2px]"
        onClick={onClose}
      />

      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="invoice-files-modal-title"
        className="relative z-10 flex h-[min(90vh,52rem)] w-full max-w-5xl flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-2xl"
      >
        <div className="flex shrink-0 items-start justify-between gap-4 border-b border-slate-100 px-5 py-4 sm:px-6">
          <div className="flex min-w-0 items-start gap-3">
            <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#EEF4F2] text-[#4B7F73]">
              <IconFileTypePdf size={20} stroke={1.75} />
            </div>
            <div className="min-w-0">
              <h2
                id="invoice-files-modal-title"
                className="text-lg font-semibold text-slate-900"
              >
                {hasMultiple ? "Invoice files" : `Invoice ${activeId}`}
              </h2>
              <p className="mt-0.5 text-sm text-slate-500">
                {hasMultiple
                  ? loadingCount > 0
                    ? `Loading ${loadedCount} of ${invoiceIds.length}${errorCount > 0 ? ` · ${errorCount} unavailable` : ""}…`
                    : `${invoiceIds.length} selected · ${loadedCount} loaded${errorCount > 0 ? ` · ${errorCount} unavailable` : ""}`
                  : "Preview the invoice PDF from your accounting software"}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
          >
            <IconX size={20} />
          </button>
        </div>

        {hasMultiple ? (
          <div className="shrink-0 border-b border-slate-100 bg-slate-50/60 px-4 py-2 sm:px-6">
            <div
              role="tablist"
              aria-label="Invoice files"
              className="flex gap-1.5 overflow-x-auto pb-0.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              {invoiceIds.map((id, index) => {
                const state = pdfStates[id];
                const isActive = index === activeIndex;
                const tabStatus =
                  state?.status === "loading"
                    ? "loading"
                    : state?.status === "error"
                      ? "error"
                      : state?.status === "success"
                        ? "ready"
                        : "pending";

                return (
                  <button
                    key={id}
                    type="button"
                    role="tab"
                    aria-selected={isActive}
                    aria-controls={`invoice-pdf-panel-${id}`}
                    id={`invoice-pdf-tab-${id}`}
                    onClick={() => setActiveIndex(index)}
                    className={`inline-flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-white text-[#3F6C61] shadow-sm ring-1 ring-slate-200"
                        : "text-slate-600 hover:bg-white/70 hover:text-slate-900"
                    }`}
                  >
                    {tabStatus === "loading" ? (
                      <IconLoader2 size={14} className="animate-spin" />
                    ) : tabStatus === "error" ? (
                      <IconAlertCircle size={14} className="text-amber-600" />
                    ) : (
                      <IconFileTypePdf size={14} className="opacity-70" />
                    )}
                    <span className="max-w-32 truncate sm:max-w-40">
                      {id}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        ) : null}

        <div
          id={`invoice-pdf-panel-${activeId}`}
          role="tabpanel"
          aria-labelledby={
            hasMultiple ? `invoice-pdf-tab-${activeId}` : undefined
          }
          className="flex min-h-0 flex-1 flex-col bg-slate-50 p-4 sm:p-6"
        >
          {activeState.status === "loading" ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 text-sm text-slate-500">
              <IconLoader2 size={32} className="animate-spin text-[#4B7F73]" />
              <p className="font-medium">Loading invoice file…</p>
            </div>
          ) : activeState.status === "error" ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-amber-50 text-amber-600">
                <IconAlertCircle size={24} />
              </div>
              <div>
                <p className="font-medium text-slate-900">
                  Could not load invoice {activeId}
                </p>
                <p className="mt-1 max-w-sm text-sm text-slate-500">
                  {activeState.message}
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  const generation = ++loadGenerationRef.current;
                  void loadPdfs([activeId], generation);
                }}
                className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                <IconRefresh size={16} />
                Try again
              </button>
            </div>
          ) : (
            <iframe
              key={activeId}
              src={`${activeState.url}#toolbar=0&navpanes=0`}
              title={`Invoice ${activeId} PDF`}
              className="h-full min-h-80 w-full flex-1 rounded-lg border border-slate-200 bg-white"
            />
          )}
        </div>

        <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-t border-slate-100 px-5 py-3 sm:px-6">
          <p className="text-xs text-slate-400">
            {hasMultiple
              ? "Use tabs or ← → keys to switch invoices"
              : "Esc to close"}
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleOpenInNewTab}
              disabled={activeState.status !== "success"}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <IconExternalLink size={16} />
              Open in tab
            </button>
            <button
              type="button"
              onClick={handleDownload}
              disabled={activeState.status !== "success"}
              className="inline-flex items-center gap-2 rounded-lg bg-[#4B7F73] px-3 py-2 text-sm font-medium text-white transition-colors hover:bg-[#3F6C61] disabled:cursor-not-allowed disabled:opacity-50"
            >
              <IconDownload size={16} />
              Download
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}
